/**
 * AI cloud-provider transport dimension for the `claude-sdk` driver.
 *
 * `cloud` is a config axis orthogonal to `provider` (model vendor) and
 * `driver` (agent runtime). Under `driver: claude-sdk` each non-default value
 * maps to one of the Claude Agent SDK's native env-var transports:
 * Amazon Bedrock, Google Vertex AI, Azure AI Foundry, or a generic
 * `ANTHROPIC_BASE_URL` gateway (LiteLLM / corporate proxy).
 *
 * Cross-repo contract: env key names and semantics here are load-bearing —
 * the platform resolves credential bundles against the same key set.
 */

/**
 * Canonical list of supported cloud transports. `"default"` (or an absent
 * `cloud` field) means the provider's native API — today's behavior, unchanged.
 *
 * The codec in `core/workspace-config.ts` keeps a local copy (`KNOWN_CLOUDS`)
 * to avoid an import cycle; a drift test asserts parity.
 *
 * @since 1.3.0
 */
export const SUPPORTED_CLOUDS = ["default", "bedrock", "vertex", "azure", "gateway"] as const;

/** One of the supported cloud transport identifiers. */
export type CloudId = (typeof SUPPORTED_CLOUDS)[number];

/**
 * Non-secret transport settings for a cloud. Sourced from skaile.yaml
 * `cloud_config` (snake_case on the wire, camelCase in-process — converted
 * exactly once at the settings/serve boundary).
 *
 * Must stay field-identical with core's `CloudTransportConfig` and the inline
 * `AgentSessionConfig.cloudConfig` shape (kept separate to avoid a core↔bridge
 * import cycle) — a field added here must be added there too.
 *
 * @since 1.3.0
 */
export interface CloudConfig {
  /** bedrock: AWS region (`AWS_REGION`); vertex: `CLOUD_ML_REGION`. */
  region?: string;
  /** vertex only: GCP project (`ANTHROPIC_VERTEX_PROJECT_ID`). */
  projectId?: string;
  /** azure only: Foundry resource name (`ANTHROPIC_FOUNDRY_RESOURCE`). */
  resource?: string;
  /** azure (alternative to `resource`) / gateway (required): endpoint base URL. */
  baseUrl?: string;
}

/**
 * Secret env keys that make up the credential bundle for each non-default
 * cloud. The platform delivers these env-keyed in the `session_init` secrets
 * map; the runner picks them out via `resolveCloudSecretBundle`.
 *
 * Vertex note: `GOOGLE_APPLICATION_CREDENTIALS_JSON` is our convention for the
 * raw service-account JSON blob. The runner materializes it to a 0600 file and
 * hands the driver `GOOGLE_APPLICATION_CREDENTIALS` (a path) instead — the
 * driver never sees the raw JSON.
 *
 * @since 1.3.0
 */
export const CLOUD_SECRET_ENV_KEYS: Record<Exclude<CloudId, "default">, readonly string[]> = {
  bedrock: [
    "AWS_ACCESS_KEY_ID",
    "AWS_SECRET_ACCESS_KEY",
    "AWS_SESSION_TOKEN",
    "AWS_BEARER_TOKEN_BEDROCK",
  ],
  vertex: ["GOOGLE_APPLICATION_CREDENTIALS_JSON"],
  azure: ["ANTHROPIC_FOUNDRY_API_KEY"],
  gateway: ["ANTHROPIC_AUTH_TOKEN", "ANTHROPIC_API_KEY"],
};

/**
 * Env vars scrubbed from the inherited environment before a non-default cloud
 * transport is assembled. Ambient first-party credentials or a stale
 * `CLAUDE_CODE_USE_*` flag must never leak into a Bedrock/Vertex/Foundry/
 * gateway session (the gateway branch re-adds its own carriers explicitly).
 *
 * Deliberately NOT exhaustive across clouds: ambient cloud-specific vars
 * (AWS_*, GOOGLE_*, ANTHROPIC_VERTEX_PROJECT_ID, ANTHROPIC_FOUNDRY_*) survive
 * so a standalone CLI inherits its own cloud's environment; the SDK ignores
 * vars whose transport flag is unset.
 */
const SCRUBBED_ENV_KEYS = ["ANTHROPIC_API_KEY", "ANTHROPIC_AUTH_TOKEN", "ANTHROPIC_BASE_URL"];

/**
 * Assemble the Claude Agent SDK child env for a non-default cloud transport.
 *
 * Pure function (no external state touched; the returned `env` is a fresh
 * object — inputs are never mutated): starts from `baseEnv` (caller passes a
 * copy of `process.env` with undefined stripped) merged with `configEnv`,
 * scrubs inherited Anthropic
 * credential/routing vars and every `CLAUDE_CODE_USE_*` flag, then sets exactly
 * one `CLAUDE_CODE_USE_*` flag (none for gateway) plus the cloud's credential
 * bundle and derived non-secret vars. AWS_* / GOOGLE_* ambient values are NOT
 * scrubbed, so a standalone CLI inherits ambient cloud credentials when no
 * explicit `cloudSecrets` bundle is provided. Gateway is the mirror case: its
 * carriers ARE the scrubbed Anthropic vars, so the gateway branch restores
 * them from `cloudSecrets` → `fallbackApiKey` (resolved `apiKeys.anthropic`)
 * → the pre-scrub config/ambient value — otherwise a standalone gateway
 * session would have no reachable auth carrier at all.
 *
 * Never returns `undefined` values; absent settings are simply omitted.
 *
 * @since 1.3.0
 */
export function buildClaudeCloudEnv(args: {
  cloud: Exclude<CloudId, "default">;
  cloudConfig?: CloudConfig;
  cloudSecrets?: Record<string, string>;
  baseEnv: Record<string, string>;
  configEnv?: Record<string, string>;
  /** Resolved API key (e.g. `apiKeys.anthropic`) — gateway auth fallback only. */
  fallbackApiKey?: string;
}): Record<string, string> {
  const { cloud, cloudConfig, cloudSecrets, baseEnv, configEnv, fallbackApiKey } = args;
  const env: Record<string, string> = { ...baseEnv, ...(configEnv ?? {}) };

  // Captured before the scrub — gateway falls back to these carriers.
  const ambient = {
    authToken: env.ANTHROPIC_AUTH_TOKEN,
    apiKey: env.ANTHROPIC_API_KEY,
    baseUrl: env.ANTHROPIC_BASE_URL,
  };

  for (const key of Object.keys(env)) {
    if (SCRUBBED_ENV_KEYS.includes(key) || key.startsWith("CLAUDE_CODE_USE_")) {
      delete env[key];
    }
  }

  const set = (key: string, value: string | undefined) => {
    // Falsy check is deliberate: an empty-string credential/setting is never
    // valid env — dropping it beats shipping a key the SDK treats as set.
    if (value) env[key] = value;
  };

  switch (cloud) {
    case "bedrock": {
      env.CLAUDE_CODE_USE_BEDROCK = "1";
      // Explicit bundle values override ambient AWS_* (which survive the scrub
      // so a standalone CLI keeps its ambient credentials).
      for (const key of CLOUD_SECRET_ENV_KEYS.bedrock) set(key, cloudSecrets?.[key]);
      set("AWS_REGION", cloudConfig?.region);
      break;
    }
    case "vertex": {
      env.CLAUDE_CODE_USE_VERTEX = "1";
      // Runner-materialized SA file path; ambient GOOGLE_APPLICATION_CREDENTIALS
      // is kept when no bundle value is provided.
      set("GOOGLE_APPLICATION_CREDENTIALS", cloudSecrets?.GOOGLE_APPLICATION_CREDENTIALS);
      set("ANTHROPIC_VERTEX_PROJECT_ID", cloudConfig?.projectId);
      set("CLOUD_ML_REGION", cloudConfig?.region);
      break;
    }
    case "azure": {
      env.CLAUDE_CODE_USE_FOUNDRY = "1";
      set("ANTHROPIC_FOUNDRY_API_KEY", cloudSecrets?.ANTHROPIC_FOUNDRY_API_KEY);
      // Resource wins over base_url when both are configured.
      if (cloudConfig?.resource) {
        env.ANTHROPIC_FOUNDRY_RESOURCE = cloudConfig.resource;
      } else {
        set("ANTHROPIC_FOUNDRY_BASE_URL", cloudConfig?.baseUrl);
      }
      break;
    }
    case "gateway": {
      // No CLAUDE_CODE_USE_* flag — the gateway is addressed purely via
      // ANTHROPIC_BASE_URL plus an auth carrier. Explicit bundle wins;
      // standalone sessions fall back to the resolved key / ambient carriers.
      set("ANTHROPIC_BASE_URL", cloudConfig?.baseUrl ?? ambient.baseUrl);
      const authToken = cloudSecrets?.ANTHROPIC_AUTH_TOKEN ?? ambient.authToken;
      if (authToken) {
        env.ANTHROPIC_AUTH_TOKEN = authToken;
      } else {
        set(
          "ANTHROPIC_API_KEY",
          cloudSecrets?.ANTHROPIC_API_KEY ?? fallbackApiKey ?? ambient.apiKey,
        );
      }
      break;
    }
  }

  return env;
}
